import classNames, { Argument } from "classnames/dedupe";
import React, { ReactNode, useCallback, useState } from "react";
import useWatchState from "../../hooks/useWatchState";
import { ShopButtonIconRed } from "../../icons";
import CompareButton from "../ActionsSelectors/Button/CompareButton/CompareButton";
import Link from "../ActionsSelectors/Link/Link";
import WishlistButton from "../ActionsSelectors/Wishlist/WishlistButton";
import ColorPicker from "../Atoms/ColorPicker/ColorPicker";
import Badge from "../DataDisplay/Badge/Badge";
import ProductPrice from "../DataDisplay/Price/ProductPrice";
import ProductReviews from "../PDP/ProductReviews/ProductReviews";
import { Image } from "../Utility/MediaBuilder/Image";
import AddToCartButton from "./AddToCartButton/AddToCartButton";
import { IProductCardData, IProductItem } from "./ProductCard.interface";
import ProductIntro from "./ProductIntro/ProductIntro";
import ProductPreviewsList from "./ProductPreviewsList/ProductPreviewsList";
import { Typography } from "../Fundamentals";

export interface IProductCardProps<LinkType = HTMLLinkElement> {
  /**
   * The type used for ColorItem presentation
   *
   * @type {IProductCardData}
   */
  productCardData: IProductCardData<LinkType>;
  /**
   * Hide favorite button for cases when there s no need to save item to wishlist
   */
  isFavoriteAvailable?: boolean;
  /**
   * Hide addToCartButton button for cases when there s no need to add item to shopping bag
   */
  isAddToCartAvailable?: boolean;
  /** Enable/disable toggle of bottom content  */
  enableToggle?: boolean;
  showColorPicker?: boolean;
  showPricing?: boolean;
  showReviews?: boolean;
  showPreviews?: boolean;
  showShopButtonRed?: boolean;
  /**
   * Toggle comparison checkbox
   */
  isComparisonAvailable?: boolean;
  /**
   * Custom styles
   */
  classes?: { root?: Argument };
  /**
   * Children nodes of current Link component
   */
  children?: ReactNode;
  /**
   *  Function to be called when Add to Cart button is clicked
   */
  onAddToCart?(
    itemId: IProductCardData<LinkType>["id"],
    isAddedToCart: boolean,
  ): void;
  /**
   *  Function to be called when Favorite button is clicked
   */
  onFavourite?(
    itemId: IProductCardData<LinkType>["id"],
    isFavourite: boolean,
  ): void;
  /**
   *  Function to be called when Comparison button is clicked
   */
  onComparison?(
    itemId: IProductCardData<LinkType>["id"],
    inComparison: boolean,
  ): void;
  /**
   * Function to be called when item in preview is clicked
   */
  onPreviewItemClick?(itemId: IProductCardData<LinkType>["id"]);
}

// eslint-disable-next-line complexity
function ProductCard<LinkType>({
  productCardData,
  productCardData: {
    colors,
    description,
    productItems,
    favorite,
    inCart,
    inComparison,
    linkParams,
  },
  isFavoriteAvailable = true,
  isAddToCartAvailable = false,
  isComparisonAvailable = false,
  enableToggle = true,
  classes,
  showPricing = true,
  showReviews = true,
  showPreviews = true,
  showColorPicker = true,
  showShopButtonRed = false,
  children,
  onAddToCart,
  onFavourite,
  onComparison,
  onPreviewItemClick,
}: IProductCardProps<LinkType>): JSX.Element {
  const [isFavouriteState, setIsFavoriteState] = useWatchState<boolean>({
    value: favorite,
  });

  const [isAddedToCartState, setIsAddedToCartState] = useWatchState<boolean>({
    value: inCart,
  });

  const [isComparingState, setIsComparingState] = useWatchState<boolean>({
    value: inComparison,
  });

  const [selectedItem, setSelectedItem] = useState<IProductItem>(
    productItems?.[0] || null,
  );

  const [activeItem] = useState<IProductItem>(
    productItems?.find((item) => item.isActive === true),
  );

  const [isHovered, setHover] = useState<boolean>(false);

  const [isFavouriteAnimating, setIsFavouriteAnimating] = useState(false);

  const handleHoverEnter = useCallback(() => {
    if ((colors?.length || 0) < 2) {
      return;
    }

    setHover(true);
  }, [colors]);

  const handleHoverLeave = useCallback(() => {
    setHover(false);
  }, []);

  const handleMainVariantOnPreviewHover = useCallback(
    (id: string) =>
      setSelectedItem(productItems.find((variant) => variant.id === id)),
    [productItems],
  );

  const handleOnPreviewClick = useCallback((id: string) => {
    onPreviewItemClick?.(id);
  }, []);

  const allowedToToggle =
    !activeItem && enableToggle && productItems?.length > 1;

  const allowedColorPicker =
    !activeItem && showColorPicker && colors?.length > 1;

  const rootClasses = classNames(
    "product-card",
    {
      "product-card--with-toggle": allowedToToggle,
      "product-card--disable-toggle": !allowedToToggle,
      "colors-preview": colors?.length > 0 ? isHovered : null,
      "z-index-max": isFavouriteAnimating,
    },
    classes?.root,
  );

  const handleFavouriteClick = useCallback(
    (e) => {
      /** !!! Warning - prevented event !!! */
      e.preventDefault();
      setIsFavoriteState(!isFavouriteState);
      !isFavouriteState && setIsFavouriteAnimating(true);
      onFavourite?.(productCardData.id, !isFavouriteState);
    },
    [isFavouriteState, onFavourite, productCardData.id, setIsFavoriteState],
  );

  const handleAddToCartClick = useCallback(() => {
    setIsAddedToCartState(true);
    onAddToCart?.(productCardData.id, true);
  }, [onAddToCart, productCardData.id, setIsAddedToCartState]);

  const handleComparison = useCallback(() => {
    onComparison?.(productCardData.id, !isComparingState);
    setIsComparingState(!isComparingState);
  }, [isComparingState, onComparison, productCardData, setIsComparingState]);

  const handleFavouriteAnimation = useCallback((newAnimState) => {
    setIsFavouriteAnimating(newAnimState);
  }, []);

  const onProductInfoClick = useCallback(
    (e: MouseEvent) => {
      const { href } = (linkParams as Partial<HTMLLinkElement>) ?? {};

      if (href) {
        globalThis.location?.assign(href);
      }

      e.preventDefault();
    },
    [linkParams],
  );

  if (!selectedItem) return null;

  return (
    <div className={rootClasses}>
      <figure>
        <Link<LinkType> {...(linkParams || {})}>
          <div
            data-testid="product-card__TestID"
            className="product-card__view"
            onMouseEnter={handleHoverEnter}
            onMouseLeave={handleHoverLeave}
          >
            <div className="product-card__main-view">
              <div className="product-card__main-image">
                {activeItem?.customImage}
                {selectedItem.customImage}
                {activeItem?.customImage || selectedItem.customImage ? null : (
                  <Image
                    data-testid="product-card__main-image"
                    src={activeItem?.image?.src ?? selectedItem.image?.src}
                    alt={activeItem?.image?.alt ?? selectedItem.image?.alt}
                  />
                )}
              </div>

              {allowedColorPicker && <ColorPicker colors={colors} />}

              {isFavoriteAvailable ? (
                <div
                  data-testid="ProductCard__favorite"
                  className="product-card__action"
                  onClick={handleFavouriteClick}
                >
                  <WishlistButton
                    isAnimating={isFavouriteAnimating}
                    isFavorite={isFavouriteState}
                    onAnimation={handleFavouriteAnimation}
                  />
                </div>
              ) : null}

              {isComparisonAvailable ? (
                <div className="product-card__action">
                  <CompareButton
                    active={isComparingState}
                    onClick={handleComparison}
                  />
                </div>
              ) : null}

              {isAddToCartAvailable ? (
                <div
                  className="product-card__action"
                  onClick={handleAddToCartClick}
                >
                  <AddToCartButton isAddedToCart={isAddedToCartState} />
                </div>
              ) : null}

              {selectedItem.badges && (
                <div className="product-badges">
                  {selectedItem.badges.map((badge) => (
                    <Badge
                      key={badge.color}
                      color={badge.color}
                      text={badge.text}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Link>
        <figcaption
          className={classNames("product-card__description", {
            "product-card__description--with-toggle": allowedToToggle,
            "product-card__description--disable-toggle": !allowedToToggle,
            "product-card__description--preview-only": activeItem,
            "product-card__description--no-reviews": !showReviews,
          })}
          data-testid="product-card__Description"
        >
          {showPreviews && productItems && (
            <ProductPreviewsList
              selectedItem={activeItem || selectedItem}
              productItems={productItems}
              onItemClick={handleOnPreviewClick}
              onItemHover={activeItem ? null : handleMainVariantOnPreviewHover}
            />
          )}
          <Typography
            classes={{
              root: "product-card__product-info text-decoration-none text-color-blue cursor-pointer",
            }}
            data-testid="product-card__info"
            onClick={onProductInfoClick}
          >
            <ProductIntro description={description} />
            {showReviews || (showPricing && selectedItem.prices?.length) ? (
              <div className="product-numbers">
                {showReviews && (
                  <ProductReviews
                    reviews={selectedItem.feedback?.reviews}
                    rating={selectedItem.feedback?.rating}
                  />
                )}
                {showPricing && selectedItem.prices?.length ? (
                  <ProductPrice prices={selectedItem.prices} />
                ) : null}
              </div>
            ) : null}
            {showShopButtonRed ? (
              <div className="product-shop-button-red">
                <ShopButtonIconRed />
              </div>
            ) : null}
          </Typography>
        </figcaption>
      </figure>
      {children}
    </div>
  );
}

export default ProductCard;
